import React, { useEffect, useState } from 'react';
import { Rocket, TrendingDown } from 'lucide-react';
import { apiClient } from '../../api/client';
import { logger } from '../../lib/logger';
import LivePrice from './LivePrice';

const fmt = (v) => {
    if (v === null || v === undefined) return '—';
    const n = Number(v);
    return `$${n.toLocaleString('en-US', { maximumFractionDigits: n >= 1000 ? 2 : n >= 1 ? 4 : 8 })}`;
};
const pct = (v) => `${Number(v) > 0 ? '+' : ''}${Number(v).toFixed(2)}%`;

function useImpulseHistory() {
    const [hist, setHist] = useState(null);
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            try {
                const { data } = await apiClient.get('/radar/stats');
                if (!cancelled) setHist(data?.by_stage?.ILK_HAREKET || null);
            } catch (e) {
                logger.warn('impulse history failed', e);
            }
        };
        load();
        const id = setInterval(load, 300000);
        return () => { cancelled = true; clearInterval(id); };
    }, []);
    return hist;
}

const Card = ({ row, onSelect, tick }) => {
    const imp = row.impulse;
    const isLong = imp.side === 'LONG';
    const Icon = isLong ? Rocket : TrendingDown;
    const tone = isLong ? 'border-emerald-500/40 bg-emerald-950/25' : 'border-rose-500/40 bg-rose-950/25';
    const cls = isLong ? 'text-emerald-300' : 'text-rose-300';
    return (
        <button
            type="button"
            onClick={() => onSelect?.(row.symbol)}
            data-testid={`impulse-card-${row.symbol}`}
            className={`text-left shrink-0 w-[86vw] sm:w-[300px] snap-center rounded-lg border p-3 space-y-1.5 hover:brightness-110 transition ${tone}`}
        >
            <div className="flex items-center gap-1.5">
                <Icon className={`h-4 w-4 ${cls}`} />
                <span className="font-mono font-bold text-base text-slate-50">{row.symbol.replace('USDT', '')}</span>
                <span className={`text-[10px] font-bold uppercase ${cls}`}>{imp.side} · İLK HAREKET</span>
                <span className="ml-auto font-mono text-xs text-slate-100">
                    <LivePrice symbol={row.symbol} tick={tick} fallbackPrice={row.price} />
                </span>
            </div>
            <div className="text-[11px] text-slate-200 leading-snug" data-testid={`impulse-text-${row.symbol}`}>{imp.text_tr}</div>
            <div className="text-[10px] text-slate-400">{imp.htf_tr} · güç {imp.strength}</div>
            <div className="rounded-md border border-slate-600/70 bg-slate-950/60 px-2 py-1.5 text-[10px] font-mono text-slate-200 space-y-0.5" data-testid={`impulse-plan-${row.symbol}`}>
                <div className="text-slate-400 font-sans text-[9px] uppercase tracking-wider">Plan · küçük pozisyon</div>
                <div>Giriş {fmt(imp.entry_low)} – {fmt(imp.entry_high)} <span className="text-slate-500 font-sans">({imp.entry_tr})</span></div>
                <div>Stop {fmt(imp.stop)} ({pct(imp.stop_pct)}) <span className="text-slate-500 font-sans">· {imp.stop_tr}</span></div>
                <div>TP1 {fmt(imp.tp1)} ({pct(imp.tp1_pct)}) · TP2 {fmt(imp.tp2)} ({pct(imp.tp2_pct)}) · R/R {imp.rr}</div>
                <div className={`font-sans font-semibold ${cls}`}>Kaldıraç {imp.leverage.label} <span className="text-slate-500 font-normal">— {imp.leverage.reason_tr}</span></div>
            </div>
        </button>
    );
};

/** İLK HAREKET — the start of a move (volume burst + 2-5% impulse + rising OI, 24h still <8%). */
export default function ImpulseStrip({ rows, onSelect, tickPrices }) {
    const hist = useImpulseHistory();
    const picks = (rows || []).filter((r) => r.impulse).sort((a, b) => (b.impulse.strength || 0) - (a.impulse.strength || 0));
    const histTxt = hist && hist.n >= 5 && hist.rate_pct !== null ? `geçmişte %${hist.rate_pct} tuttu (${hist.n})` : `geçmiş toplanıyor (${hist?.n || 0})`;
    if (picks.length === 0) {
        return (
            <div data-testid="impulse-strip-empty" className="border border-slate-700/80 bg-slate-800 rounded-md px-4 py-3 flex items-center gap-3 flex-wrap">
                <Rocket className="h-4 w-4 text-slate-400" />
                <span className="text-[11px] text-slate-400 uppercase tracking-wider">İlk Hareket</span>
                <span className="text-xs text-slate-400">· Şu an yeni başlayan hareket yok — hacim + ilk ivme + OI artışı yakalanınca burada ve Telegram'da görünür.</span>
                <span className="ml-auto text-[10px] text-slate-500" data-testid="impulse-history">{histTxt}</span>
            </div>
        );
    }
    return (
        <div data-testid="impulse-strip" className="border border-emerald-500/25 bg-slate-800 rounded-xl overflow-hidden">
            <div className="px-4 py-2.5 border-b border-slate-700 flex items-center gap-x-3 gap-y-1 flex-wrap">
                <Rocket className="h-4 w-4 text-emerald-400" />
                <span className="text-[11px] uppercase tracking-[0.2em] text-emerald-200 font-semibold">İlk Hareket · tren istasyonda</span>
                <span className="text-[10px] text-slate-400">hacim x2+ · 1 saatte %2-5 · OI artıyor · 24s &lt;%8 — küçük pozisyon, resmi sinyal değil</span>
                <span className="ml-auto text-[10px] text-slate-300" data-testid="impulse-history">{histTxt} · {picks.length} coin</span>
            </div>
            <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory p-3">
                {picks.map((r) => <Card key={r.symbol} row={r} onSelect={onSelect} tick={tickPrices?.[r.symbol]} />)}
            </div>
        </div>
    );
}
